import { QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from 'react-router-dom';
import { PlanGate } from './components/PlanGate';
import { GuestRoute, ProtectedRoute } from './components/ProtectedRoute';
import { SkeletonCard } from './components/ui/Skeleton';
import { queryClient } from './lib/queryClient';
import LandingPage from './pages/landing/LandingPage';
import ErrorPage from './pages/system/ErrorPage';
import { useAuthStore } from './store/authStore';

// Public
const AntdProviders = lazy(() => import('./components/AntdProviders'));
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage'));
const ForgotPasswordPage = lazy(
  () => import('./pages/auth/ForgotPasswordPage'),
);
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfUsePage = lazy(() => import('./pages/TermsOfUsePage'));
const NotFoundPage = lazy(() => import('./pages/system/NotFoundPage'));

// App (the shell carries antd Drawer/Dropdown, the create-link modal and the command palette)
const AppShell = lazy(() =>
  import('./components/layout/AppShell').then((m) => ({ default: m.AppShell })),
);
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'));
const LinksListPage = lazy(() => import('./pages/links/LinksListPage'));
const LinkAnalyticsPage = lazy(() => import('./pages/links/LinkAnalyticsPage'));
const ApiKeysPage = lazy(() => import('./pages/api-keys/ApiKeysPage'));
const ProfilePage = lazy(() => import('./pages/profile/ProfilePage'));
const SubscriptionPage = lazy(
  () => import('./pages/subscription/SubscriptionPage'),
);
const UiPreviewPage = import.meta.env.DEV
  ? lazy(() => import('./pages/dev/UiPreviewPage'))
  : null;

const PageFallback = () => (
  <div className="mx-auto flex max-w-[1400px] flex-col gap-4 p-8">
    <SkeletonCard height="h-12" className="max-w-md" />
    <SkeletonCard height="h-72" />
  </div>
);

const Public = ({ children }: { children: ReactNode }) => (
  <Suspense fallback={<PageFallback />}>{children}</Suspense>
);

/** Legacy `/analytics?linkId=X` → `/app/links/X`. */
function LegacyAnalyticsRedirect() {
  const { search } = useLocation();
  const linkId = new URLSearchParams(search).get('linkId');
  return <Navigate to={linkId ? `/app/links/${linkId}` : '/app'} replace />;
}

/** Legacy `/links/:id` (if ever linked) → `/app/links/:id`. */
function LegacyLinkRedirect() {
  const { id } = useParams();
  return <Navigate to={`/app/links/${id}`} replace />;
}

export function AppRoutes() {
  return (
    <Routes>
      {/* Public surface */}
      <Route path="/" element={<LandingPage />} />
      <Route
        path="/login"
        element={
          <GuestRoute>
            <Public>
              <LoginPage />
            </Public>
          </GuestRoute>
        }
      />
      <Route
        path="/register"
        element={
          <GuestRoute>
            <Public>
              <RegisterPage />
            </Public>
          </GuestRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <Public>
            <ForgotPasswordPage />
          </Public>
        }
      />
      <Route
        path="/reset-password"
        element={
          <Public>
            <ResetPasswordPage />
          </Public>
        }
      />
      <Route
        path="/privacy-policy"
        element={
          <Public>
            <PrivacyPolicyPage />
          </Public>
        }
      />
      <Route
        path="/terms-of-use"
        element={
          <Public>
            <TermsOfUsePage />
          </Public>
        }
      />
      <Route
        path="/error"
        element={
          <Public>
            <ErrorPage />
          </Public>
        }
      />
      {/* antd theme/message providers are mounted only for the app (and the dev catalogue) */}
      <Route
        element={
          <Public>
            <AntdProviders />
          </Public>
        }
      >
        {UiPreviewPage && (
          <Route
            path="/dev/ui"
            element={
              <Public>
                <UiPreviewPage />
              </Public>
            }
          />
        )}
        <Route
          path="/app"
          element={
            <ProtectedRoute>
              <Suspense fallback={<PageFallback />}>
                <AppShell />
              </Suspense>
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="links" element={<LinksListPage />} />
          <Route path="links/:id" element={<LinkAnalyticsPage />} />
          {/* Account-wide analytics live on the dashboard; keep the path for old links. */}
        <Route path="analytics" element={<Navigate to="/app" replace />} />
          <Route
            path="api-keys"
            element={
              <PlanGate
                plans={['pro', 'growth', 'business']}
                feature="API access"
              >
                <ApiKeysPage />
              </PlanGate>
            }
          />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="subscription" element={<SubscriptionPage />} />
        </Route>
      </Route>

      {/* Legacy redirects (old bookmarks keep working) */}
      <Route path="/dashboard" element={<Navigate to="/app" replace />} />
      <Route
        path="/links/create"
        element={<Navigate to="/app/links?new=1" replace />}
      />
      <Route path="/links/:id" element={<LegacyLinkRedirect />} />
      <Route path="/links" element={<Navigate to="/app/links" replace />} />
      <Route path="/analytics" element={<LegacyAnalyticsRedirect />} />
      <Route
        path="/api-keys"
        element={<Navigate to="/app/api-keys" replace />}
      />
      <Route path="/profile" element={<Navigate to="/app/profile" replace />} />
      <Route
        path="/subscription"
        element={<Navigate to="/app/subscription" replace />}
      />

      <Route
        path="*"
        element={
          <Public>
            <NotFoundPage />
          </Public>
        }
      />
    </Routes>
  );
}

function App() {
  const initAuth = useAuthStore((s) => s.initAuth);

  useEffect(() => {
    initAuth();
  }, [initAuth]);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
